import { Identifier, XYCoord } from 'dnd-core';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { Colors, Icon } from '@blueprintjs/core';
import Flex from '@components/Flex';

import { SUB_TICKET_DND_TYPE, SubTicketDragItem } from './types';

export interface DraggableSubTicketRowProps {
    subTicketId: ID;
    // The immutable label. Reordering deliberately does not move it, so it is what the handle is
    // named after — "Reorder row 3" would name a position that changes as you drag.
    ordinal: number;
    index: number;

    // Resolved by the page from the ordering availability gate, the row's own update permission
    // and the in-flight submission guard
    canDrag: boolean;

    // Fired continuously while hovering: the page keeps the draft order, this component keeps
    // none of it
    onHover: (draggedSubTicketId: ID, hoverIndex: number) => void;
    // Fired once, on release
    onDrop: () => void;
    // Fired when release happens outside every row, so a hover preview never becomes latent state
    onCancel: () => void;
    // The visible drag handle remains a real keyboard reorder control
    onKeyboardMove: (draggedSubTicketId: ID, targetIndex: number) => void;

    // The row is handed its own drag handle to place in its ordering slot, so this component
    // stays a behaviour wrapper and the card stays presentational
    renderRow: (handle: React.ReactNode) => React.ReactNode;
}

export type Props = DraggableSubTicketRowProps;

// A draggable row for the sub-task list.
//
// There is deliberately NO DndProvider here. The App mounts exactly one, and a nested provider
// gives the subtree its own drag manager — every drag inside it silently stops reaching the
// outer one, which looks like "drag and drop just does not work" and has no error attached.
const DraggableSubTicketRow: React.FC<Props> = ({
    subTicketId,
    ordinal,
    index,
    canDrag,
    onHover,
    onDrop,
    onCancel,
    onKeyboardMove,
    renderRow,
}) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const handleRef = useRef<HTMLDivElement | null>(null);

    const [{ isDragging }, drag, preview] = useDrag(
        {
            type: SUB_TICKET_DND_TYPE,
            canDrag,
            item: () => ({ subTicketId, index }),
            end: (_item, monitor) => {
                if (!monitor.didDrop()) {
                    onCancel();
                }
            },
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        },
        [subTicketId, index, canDrag, onCancel]
    );

    const [{ handlerId }, drop] = useDrop<SubTicketDragItem, void, { handlerId: Identifier | null }>(
        {
            accept: SUB_TICKET_DND_TYPE,
            canDrop: () => canDrag,
            hover: (item, monitor) => {
                if (!rowRef.current || !canDrag) {
                    return;
                }

                const dragIndex = item.index;
                const hoverIndex = index;

                if (dragIndex === hoverIndex) {
                    return;
                }

                // Only swap once the pointer has crossed the middle of the row it is over.
                // Without this the rows flicker between two positions for the whole drag.
                const hoverBoundingRect = rowRef.current.getBoundingClientRect();
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                const clientOffset = monitor.getClientOffset();
                const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                    return;
                }

                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                    return;
                }

                onHover(item.subTicketId, hoverIndex);

                // Mutating the monitor item, as the open-task list does: the item now IS at the
                // hover index, and re-deriving that on every mouse move would be a list search
                item.index = hoverIndex;
            },
            drop: () => {
                onDrop();
            },
            collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
        },
        [subTicketId, index, canDrag, onHover, onDrop]
    );

    drag(handleRef);
    drop(rowRef);
    preview(rowRef);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!canDrag || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
            return;
        }

        event.preventDefault();
        onKeyboardMove(subTicketId, event.key === 'ArrowUp' ? index - 1 : index + 1);
    };

    // Named by the immutable label, and reachable from the keyboard: a control that only exists
    // to a mouse is not a control
    const renderHandle = () => {
        if (!canDrag) {
            return null;
        }

        return (
            <div
                ref={handleRef}
                className="mr-1"
                role="button"
                tabIndex={0}
                aria-label={`Reorder Sub-task #${ordinal}`}
                title={`Reorder Sub-task #${ordinal}`}
                onKeyDown={handleKeyDown}
                style={{ cursor: 'move', color: Colors.GRAY3 }}
            >
                <Flex direction="row" align="center">
                    <Icon icon="drag-handle-vertical" />
                </Flex>
            </div>
        );
    };

    return (
        <div ref={rowRef} data-handler-id={handlerId} style={{ opacity: isDragging ? 0.4 : 1 }}>
            {renderRow(renderHandle())}
        </div>
    );
};

export default DraggableSubTicketRow;
